import LocationIcon from '../../assets/icons/LocationIcon';
import classnames from 'classnames';
import useTheme from '../../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import { ComponentPropsWithoutRef, FC, MouseEventHandler, useEffect, useMemo, useState } from 'react';
import CompanyDetailsModal from '../CompanyDetailsModal';
import { CompanyDistanceInterface } from './CompaniesList';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import HelperMethods from '../../services/exports/HelperMethods';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import { Radio } from '@mui/material';
import { useLocationData } from './provider/LocationDataProvider';
import RoomServiceForm from './form/RoomServiceForm';
import { ORDER_METHODS } from '../../services/exports/Constants';
import useAppMethods from '../../hooks/utility/useAppMethods';
import CheckedIcon from '../../modules/mobile/assets/icons/CheckedIcon';
import useOrderTimeOptions, { ORDER_TIME_OPTIONS, OrderTimeOption } from './useOrderTimeOptions';
import collect from 'collect.js';
import useAppState from '../../hooks/global/useAppState';

interface Props extends ComponentPropsWithoutRef<'article'> {
  data: CompanyDistanceInterface;
  showRadioButton?: boolean;
}

const CompanyCard: FC<Props> = (props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const { data, className, showRadioButton = true, ...rest } = props;
  const { company, distance } = data;
  const theme = useTheme();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { company: selectedCompany, setCompany, orderMethod, setScheduledFor } = useLocationData();
  const { is_open, details_text } = useOpeningStatus({ data: company, orderMethod });
  const isSelected = selectedCompany?.id === company?.id;
  const { toggleOrderMethodModal } = useAppMethods();
  const { isScheduledModalOpen } = useAppState().orderMethodModalProps;
  const { timeOptions, selectedTimeOption, setSelectedTimeOption } = useOrderTimeOptions();

  useEffect(() => {
    if (isSelected && !selectedTimeOption && collect(timeOptions).firstWhere('id', ORDER_TIME_OPTIONS.asap)) {
      setSelectedTimeOption(ORDER_TIME_OPTIONS.asap);
    }
  }, [timeOptions, isSelected, selectedTimeOption]);

  useEffect(() => {
    if (selectedCompany?.id !== company?.id) {
      return;
    }

    if (!isScheduledModalOpen) {
      return;
    }

    toggleOrderMethodModal({
      isScheduledModalOpen: true,
      openModal: true,
      company,
    });
  }, [company]);

  const toggleInfoModal = () => setShowInfoModal((prev) => !prev);
  const renderCompanyInfoModal = useMemo(
    () =>
      showInfoModal && <CompanyDetailsModal data={company} openModal={showInfoModal} toggleModal={toggleInfoModal} />,
    [company, showInfoModal],
  );
  const onTimeOptionClick = (timeOption: ORDER_TIME_OPTIONS) => {
    if (timeOption === ORDER_TIME_OPTIONS.asap) {
      setScheduledFor(null);
      setSelectedTimeOption(timeOption);
      return;
    }

    toggleOrderMethodModal({
      isScheduledModalOpen: true,
      openModal: true,
      company: selectedCompany,
    });
  };
  const onClickInfo: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    toggleInfoModal();
  };
  const hasRoomServiceForm = orderMethod === ORDER_METHODS.ROOM_SERVICE && company?.id === selectedCompany?.id;

  const renderTimeOption = (item: OrderTimeOption) => {
    if (item.id === ORDER_TIME_OPTIONS.asap) {
      return (
        <div className="w-full flex justify-between items-center">
          <div className="flex items-center gap-x-sm">
            {item.active && <CheckedIcon color={theme.color.brand.text.primary} className="stroke-2" />}
            <p className="text-sm">{item.title}</p>
          </div>
          {item.description && <p className="text-sm">{item.description}</p>}
        </div>
      );
    }

    return (
      <div className="w-full flex justify-between items-center">
        <div className="flex items-center gap-x-sm">
          {item.active && <CheckedIcon color={theme.color.brand.text.primary} className="stroke-2" />}
          <p className="text-sm">{item.description ?? item.title}</p>
        </div>
        <p className="ml-auto underline text-sm">{t('buttons.change')}</p>
      </div>
    );
  };

  return (
    <article className={classnames('text-brand-text-primary cursor-pointer', className)} {...rest}>
      <div
        onClick={() => {
          if (!isSelected) {
            setCompany(company);
            setScheduledFor(null);
            setSelectedTimeOption(null);
          }
        }}
        className="space-y-[2px]"
      >
        <div className="flex justify-between items-center gap-x-sm">
          <h6>{company?.name}</h6>
          {!!distance && (
            <p className="text-sm ml-auto text-brand-primary bg-brand-primary/20 p-1 rounded-[4px] flex-shrink-0">
              {HelperMethods.formatDistance(distance)}
            </p>
          )}
          {showRadioButton && <Radio checked={isSelected} />}
        </div>
        <h6
          className={classnames('text-sm', {
            'text-primary': is_open,
            'text-orange': !is_open,
          })}
        >
          {details_text}
        </h6>
        <div className="flex items-center space-x-1">
          <LocationIcon className="size-4" color={theme.color.gray['700']} />
          <p className="text-sm">
            {AddressHelpers.setCountry(company?.country).format({
              street_name: company?.address,
              city: company?.city,
              zip_code: company?.zip_code,
            })}
          </p>
        </div>
        <div onClick={onClickInfo} className="w-fit">
          <p className="underline text-sm">{t('buttons.see_hours')}</p>
        </div>
      </div>
      {hasRoomServiceForm && <RoomServiceForm className="mt-md" key={company?.id} />}
      {isSelected &&
        timeOptions.map((item) => (
          <article key={`time-option-${item.id}`} className="mt-md">
            <div
              className={classnames(
                'flex flex-row cursor-pointer gap-x-sm p-md border border-brand-border rounded-lg',
                {
                  'bordered !border-black dark:!border-white': item.active,
                  'hover:border-black dark:hover:border-white': !item.active,
                },
              )}
              onClick={() => onTimeOptionClick(item.id)}
            >
              {renderTimeOption(item)}
            </div>
          </article>
        ))}
      {renderCompanyInfoModal}
    </article>
  );
};

export default CompanyCard;
